/**
 * S2.3: Cegid CON API Wrapper (Contabilidad)
 * Supports both standard (tipoOpcionFiscal=1) and estimaciones (2|3|4)
 * 
 * Standard endpoints: Subcuentas, Facturas, Apuntes, Series, Vencimientos, Analitica
 * Estimaciones endpoints: Actividades, Clientes, FacturasEstimaciones, GruposGastos, GruposIngresos, Proveedores
 * Common: Autorización, Parámetros
 */

import { cegidFetch } from './auth';
import type {
  CegidFactura,
  CegidApunte,
  CegidSubcuenta,
  CegidCliente,
  CegidProveedor,
  CegidFacturaEstimacion,
  ODataParams,
} from './types';

function buildODataQuery(params?: ODataParams): Record<string, string> {
  if (!params) return {};
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      query[key] = String(value);
    }
  }
  return query;
}

function extractDatos(data: any): any[] {
  if (Array.isArray(data)) return data;
  if (data?.Datos && Array.isArray(data.Datos)) return data.Datos;
  if (data?.value && Array.isArray(data.value)) return data.value;
  return [];
}

// =============================================
// STANDARD ACCOUNTING (tipoOpcionFiscal = 1)
// =============================================

/**
 * Get chart of accounts (subcuentas).
 * GET /api/subcuentas/?Ejercicio=X&$filter=...
 */
export async function getSubcuentas(
  companyCode: string,
  ejercicio: string,
  odata?: ODataParams
): Promise<CegidSubcuenta[]> {
  const params = buildODataQuery(odata);
  const data = await cegidFetch('con', '/api/subcuentas/', {
    companyCode,
    params,
  });
  return extractDatos(data);
}

/**
 * Get invoices (facturas).
 * GET /api/facturas/?Ejercicio=X&$top=N&$skip=M
 */
export async function getFacturas(
  companyCode: string,
  ejercicio: string,
  odata?: ODataParams
): Promise<CegidFactura[]> {
  const ejercicioFilter = `Ejercicio eq '${ejercicio}'`;
  const existingFilter = odata?.['$filter'] as string | undefined;
  const mergedFilter = existingFilter ? `(${existingFilter}) and (${ejercicioFilter})` : ejercicioFilter;
  const params = buildODataQuery({ ...odata, '$filter': mergedFilter });
  const data = await cegidFetch('con', '/api/facturas/', {
    companyCode,
    params,
  });
  return extractDatos(data);
}

/**
 * Get accounting entries (apuntes/asientos).
 * GET /api/apuntes/?Ejercicio=X
 */
export async function getApuntes(
  companyCode: string,
  ejercicio: string,
  odata?: ODataParams
): Promise<CegidApunte[]> {
  const ejercicioFilter = `Ejercicio eq '${ejercicio}'`;
  const existingFilter = odata?.['$filter'] as string | undefined;
  const mergedFilter = existingFilter ? `(${existingFilter}) and (${ejercicioFilter})` : ejercicioFilter;
  const params = buildODataQuery({ ...odata, '$filter': mergedFilter });
  const data = await cegidFetch('con', '/api/apuntes/', {
    companyCode,
    params,
  });
  return extractDatos(data);
}

/**
 * Get clients from accounting.
 * GET /api/clientes/
 */
export async function getClientes(
  companyCode: string,
  odata?: ODataParams
): Promise<CegidCliente[]> {
  const params = buildODataQuery(odata);
  const data = await cegidFetch('con', '/api/clientes/', {
    companyCode,
    params,
  });
  return extractDatos(data);
}

/**
 * Get suppliers from accounting.
 * GET /api/proveedores/
 */
export async function getProveedores(
  companyCode: string,
  odata?: ODataParams
): Promise<CegidProveedor[]> {
  const params = buildODataQuery(odata);
  const data = await cegidFetch('con', '/api/proveedores/', {
    companyCode,
    params,
  });
  return extractDatos(data);
}

/**
 * Post a new invoice to Cegid.
 * POST /api/facturas/add
 */
export async function postFactura(
  companyCode: string,
  factura: Partial<CegidFactura>
): Promise<any> {
  return cegidFetch('con', '/api/facturas/add', {
    method: 'POST',
    companyCode,
    body: factura,
  });
}

/**
 * Post a new client to Cegid.
 * POST /api/clientes/add
 */
export async function postCliente(
  companyCode: string,
  cliente: Partial<CegidCliente>
): Promise<any> {
  return cegidFetch('con', '/api/clientes/add', {
    method: 'POST',
    companyCode,
    body: cliente,
  });
}

/**
 * Post a new supplier to Cegid.
 * POST /api/proveedores/add
 */
export async function postProveedor(
  companyCode: string,
  proveedor: Partial<CegidProveedor>
): Promise<any> {
  return cegidFetch('con', '/api/proveedores/add', {
    method: 'POST',
    companyCode,
    body: proveedor,
  });
}

// =============================================
// INCREMENTAL SYNC HELPERS (S3.8)
// =============================================

/**
 * Build an OData $filter clause for records modified since lastSyncAt.
 * Cegid CON uses OData v4, so date comparison uses ISO format.
 * If no lastSyncAt is provided, returns empty (full sync).
 */
export function buildIncrementalFilter(
  lastSyncAt?: string | null,
  dateField: string = 'FechaModificacion'
): string | undefined {
  if (!lastSyncAt) return undefined;
  const isoDate = new Date(lastSyncAt).toISOString();
  return `${dateField} gt ${isoDate}`;
}

/**
 * Merge an incremental $filter with any existing OData params.
 */
export function withIncrementalSync(
  odata?: ODataParams,
  lastSyncAt?: string | null,
  dateField?: string
): ODataParams | undefined {
  const filter = buildIncrementalFilter(lastSyncAt, dateField);
  if (!filter) return odata;

  const existing = odata || {};
  const currentFilter = existing['$filter'] as string | undefined;
  return {
    ...existing,
    '$filter': currentFilter ? `(${currentFilter}) and (${filter})` : filter,
  };
}

// =============================================
// ESTIMACIONES / AUTÓNOMOS (tipoOpcionFiscal 2|3|4)
// =============================================

/**
 * Get estimated invoices (for autónomos).
 * GET /api/facturasestimaciones/recibidas/?Ejercicio=X
 */
export async function getFacturasEstimaciones(
  companyCode: string,
  tipo: 'recibidas' | 'emitidas',
  ejercicio: string,
  odata?: ODataParams
): Promise<CegidFacturaEstimacion[]> {
  const params = buildODataQuery(odata);
  const data = await cegidFetch('con', `/api/facturasestimaciones/${tipo}/`, {
    companyCode,
    params,
  });
  return extractDatos(data);
}
